import { baseService, GLOBAL_TAG_TYPES } from '@repo/services';
import {
  AutomationEdgeStatusEnum, AutomationNodeStatusEnum, type AutomationStatus, transformGraphqlResponse,
} from '@repo/utils';
import { type AutomationDetailResponse, getAutomationDetail } from 'automations/queries/getAutomationDetail';
import {
  getAutomationExecution,
  type GetAutomationExecutionResponse,
} from 'automations/queries/getAutomationExecution';
import { getAutomationTree, type GetAutomationTreeResponse } from 'automations/queries/getAutomationTree';

export interface CreateAutomationRequest {
  name: string;
  description?: string;
  appearance_type?: 'hidden' | 'visible';
}

export interface CreateAutomationResponse {
  id: number;
  name: string;
  description?: string;
}

export interface UpdateAutomationRequest {
  id: string;
  name: string;
  description?: string;
}

const automationService = baseService.injectEndpoints({
  endpoints: (build) => ({
    createAutomation: build.mutation<CreateAutomationResponse, CreateAutomationRequest>({
      query: (body) => ({
        method: 'POST',
        url: '/v1/automations',
        body,
      }),
      invalidatesTags: [GLOBAL_TAG_TYPES.automationList],
    }),
    updateAutomation: build.mutation<void, UpdateAutomationRequest>({
      query: (body) => ({
        method: 'PUT',
        url: `/v1/automations/${body.id}`,
        body: {
          name: body.name,
          description: body.description,
        },
      }),
      invalidatesTags: [
        GLOBAL_TAG_TYPES.automationList,
        GLOBAL_TAG_TYPES.automationDetail,
      ],
    }),
    getAutomationTree: build.query<GetAutomationTreeResponse, { id: number; withSuccessExecutionCounts?: boolean }>({
      query: ({ id, withSuccessExecutionCounts = false }) => ({
        method: 'POST',
        url: '/graphql',
        body: {
          query: getAutomationTree(withSuccessExecutionCounts),
          variables: { automationId: id },
        },
      }),
      transformResponse: (response) => {
        const initialData = transformGraphqlResponse(response) as GetAutomationTreeResponse;

        const activeNodes = initialData.automationTree.filter(
          (node) => node.status === AutomationNodeStatusEnum.active,
        );
        const activeNodeIds = activeNodes.map((node) => node.id);
        activeNodes.forEach((node) => {
          node.sourceEdges = node.sourceEdges.filter(
            (edge) => activeNodeIds.includes(edge.targetNodeId.toString())
              && edge.status === AutomationEdgeStatusEnum.active,
          );
        });
        return { automationTree: activeNodes };
      },
      providesTags: [GLOBAL_TAG_TYPES.automationTree],
    }),
    getAutomationExecution: build.query<GetAutomationExecutionResponse, number | string>({
      query: (automationExecutionId) => ({
        method: 'POST',
        url: '/graphql',
        body: { query: getAutomationExecution, variables: { automationExecutionId } },
      }),
      transformResponse: transformGraphqlResponse,
    }),
    getAutomationDetail: build.query<AutomationDetailResponse, number | string>({
      query: (id) => ({
        method: 'POST',
        url: '/graphql',
        body: {
          query: getAutomationDetail,
          variables: {
            filterOptions: {
              id,
            },
          },
        },
      }),
      providesTags: [GLOBAL_TAG_TYPES.automationDetail],
      transformResponse: transformGraphqlResponse,
    }),
    updateAutomationStatus: build.mutation<void, { id: string; status: AutomationStatus }>({
      query: ({ id, status }) => ({
        method: 'POST',
        url: `/v1/automations/${id}/update_status`,
        body: { status },
      }),
      invalidatesTags: [
        GLOBAL_TAG_TYPES.automationTree,
        GLOBAL_TAG_TYPES.automationDetail,
        GLOBAL_TAG_TYPES.automationList,
      ],
    }),
    cloneAutomation: build.mutation<{ automation_id: string }, { id: string }>({
      query: ({ id }) => ({
        method: 'POST',
        url: `/v1/automations/${id}/clone`,
      }),
    }),
  }),
});

export const {
  useCreateAutomationMutation,
  useGetAutomationTreeQuery,
  useGetAutomationDetailQuery,
  useGetAutomationExecutionQuery,
  useUpdateAutomationStatusMutation,
  useUpdateAutomationMutation,
  useCloneAutomationMutation,
} = automationService;

export default automationService;
